import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { APP_HEADER_HEIGHT } from '../constants/layout';
import { DESK_PRIMARY_NAV_ITEMS } from '../utils/desk-primary-nav';
import { cn } from '../lib/utils';
import { Link } from './ui/link';
import { SidebarToggleButton } from './SidebarToggleButton';
import { UserAvatarMenu } from './UserAvatarMenu';
import { EditorHelpDialog } from './EditorHelpDialog';
import { DeskSearch } from './DeskSearch';

type DeskMobileNavHeaderProps = {
  actions?: React.ReactNode;
};

export const DeskMobileNavHeader: React.FC<DeskMobileNavHeaderProps> = ({ actions }) => {
  const { t } = useLocale();
  const location = useLocation();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  return (
    <header
      data-testid="desk-mobile-nav-header"
      className="bg-card text-foreground w-full max-w-full min-w-0 overflow-visible"
      style={{
        position: 'sticky',
        top: 0,
        flexShrink: 0,
        boxSizing: 'border-box',
        zIndex: 10,
      }}
    >
      <div
        data-testid="desk-mobile-nav-title-row"
        className="flex w-full items-center justify-between gap-3"
        style={{
          height: APP_HEADER_HEIGHT,
          padding: '0 1rem',
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <div className="flex min-w-0 items-center gap-1">
          <SidebarToggleButton />
          <nav className="flex min-w-0 items-center gap-1" aria-label={t('desk') || 'Desk'}>
            {DESK_PRIMARY_NAV_ITEMS.map((item) => {
              const label = t(item.labelKey) || item.fallbackLabel;
              const Icon = item.icon;
              const active = item.isActive(location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  title={label}
                  aria-label={label}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md transition-colors',
                    active
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Icon className="h-6 w-6" aria-hidden />
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            title={t('help')}
            aria-label={t('help')}
            onClick={() => setHelpDialogOpen(true)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
          >
            <HelpCircle className="h-6 w-6" aria-hidden />
          </button>
          <UserAvatarMenu variant="header" />
        </div>
      </div>
      <div
        data-testid="desk-mobile-nav-search-row"
        className={cn(
          'w-full px-4 pb-2',
          actions ? undefined : 'border-b border-border'
        )}
      >
        <DeskSearch className="max-w-none" />
      </div>
      {actions ? (
        <div
          data-testid="desk-mobile-nav-actions-row"
          className="flex w-full flex-wrap items-center justify-end gap-2 border-b border-border px-4 py-2"
        >
          {actions}
        </div>
      ) : null}
      <EditorHelpDialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen} />
    </header>
  );
};
